import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, useMediaQuery } from '@mui/material';
import {
  ThemePreference,
  cycleThemePreference,
  readThemePreference,
  resolvePaletteMode,
  writeThemePreference,
} from './themeMode';
import { createAppTheme } from './createAppTheme';
import { useI18n } from '../i18n';

interface ColorModeContextValue {
  preference: ThemePreference;
  resolvedMode: 'light' | 'dark';
  setPreference: (value: ThemePreference) => void;
  cyclePreference: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error('useColorMode must be used within ColorModeProvider');
  }
  return ctx;
}

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const { locale } = useI18n();
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readThemePreference());

  useEffect(() => {
    writeThemePreference(preference);
  }, [preference]);

  const resolvedMode = resolvePaletteMode(preference, systemDark);
  const theme = useMemo(() => createAppTheme(resolvedMode, locale), [resolvedMode, locale]);

  const value = useMemo<ColorModeContextValue>(
    () => ({
      preference,
      resolvedMode,
      setPreference: setPreferenceState,
      cyclePreference: () => setPreferenceState((prev) => cycleThemePreference(prev)),
    }),
    [preference, resolvedMode]
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
};
